import { useCallback } from "react";

import { type Refund, type RefundQueryParams, RefundQueryParamsSchema, refundsEndpoints } from "../../../api";
import { useCachedStrapiList, type UseCachedStrapiListOptions } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import handleError from "../../utils/handleError";

interface UseRefundsProps extends UseCachedStrapiListOptions<Refund> {
  mine?: boolean;
}

const useRefunds = (props?: UseRefundsProps) => {
  const { mine, ...otherProps } = props || {};

  // Stable load function - only changes when mine changes
  const isMine = !!mine;
  const load = useCallback(
    (params: Record<string, unknown>) => {
      const parsed = RefundQueryParamsSchema.parse(params);
      return loadRefunds(parsed, isMine);
    },
    [isMine]
  );

  // Stable error handler
  const onLoadError = useCallback((err: unknown) => {
    handleError(getErrorMessage(err, "Refunds loading failed"));
  }, []);

  const {
    items: refunds,
    refresh,
    isLoading,
    isRefreshing,
    ...other
  } = useCachedStrapiList<Refund>({
    ...otherProps,
    load,
    onLoadError,
  });

  return {
    refunds,
    refresh,
    isLoading,
    isRefreshing,
    ...other,
  };
};

export const loadRefunds = async (params: RefundQueryParams, mine?: boolean) => {
  try {
    // Schema validation ensures params structure is correct
    // Endpoint accepts RefundQueryParams directly (populate defaults are applied internally)
    return mine ? refundsEndpoints.getMyRefunds(params) : refundsEndpoints.getRefunds(params);
  } catch (err: unknown) {
    handleError(getErrorMessage(err, "Refunds loading failed"));
  }
};

export default useRefunds;
